import {
  ActivatedRouteSnapshot,
  Route,
  Router,
  RouterModule,
  Routes
} from '@angular/router';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  CURRENT_PROJECT_ID,
  CURRENT_STAGE_ID,
  ProjectsFacade,
  UserPreferenceFacade
} from '@simlab/data-store';

import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  inject
} from '@angular/core';
import { provideEffects } from '@ngrx/effects';
import { provideState } from '@ngrx/store';
import {
  ComponentsFacade,
  NotesFacade,
  ProjectPrivilegesEffects,
  ProjectPrivilegesFacade,
  ShowcaseNotesFacade,
  StagesFacade,
  privilegesFeatureKey,
  projectPrivilegesReducer
} from '@simlab/data-store';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { toSignal } from '@angular/core/rxjs-interop';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  getProjectProcoreInfo,
  projectFiltersProviders
} from '@simlab/pages/project/providers';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { procoreBaseInfoPayload } from '@simlab/procore/models';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { RFIPreviewConfigurableFieldsetService } from '@simlab/procore/rfi-preview-panel/data-access';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { API_URL, publicEmbeddedToken } from '@simlab/data-access';
import { ProcoreAuthorizationService } from '@simlab/procore/services';
import {
  AreaFacade,
  DistanceFacade,
  provideAreaMeasurment,
  provideDistanceMeasurment
} from '@simlab/scans/measurement/state';
import { API_URL_NEW } from '@simlab/shared/common/tokens';
import {
  Environment,
  LastKnownMatterportStateService,
  LastKnownScanStateService,
  RouterFacadeService
} from '@simlab/util-shared';
import { CheckUserGuard, UserPreferencesResolver } from '@simlab/util/core';
import { AutoLoginAllRoutesGuard } from 'angular-auth-oidc-client';
import { iif, map, of, switchMap, tap } from 'rxjs';
import { CallbackComponent } from './components/callback/callback.component';
import { ErrorComponent } from './components/error/error.component';
import { ForbiddenComponent } from './components/forbidden/forbidden.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { SocketLayoutComponent } from './components/socket-layout/socket-layout.component';
import { QueryGuard } from './guards/query.guard';

export const permissionToProjectGuard = (route: ActivatedRouteSnapshot) => {
  if (route.params['projectId']) {
    const projectPrivilegesFacade = inject(ProjectPrivilegesFacade);
    const router: Router = inject(Router);
    projectPrivilegesFacade.loadPrivileges(route.params['projectId']);
    return projectPrivilegesFacade.permissionLoaded$.pipe(
      tap((loaded) => {
        if (!loaded) {
          router.navigate([`projects/list`]);
        }
      })
    );
  }
  return of(true);
};

@Component({
  selector: 'simlab-clear-state-wrapper',
  template: ` <router-outlet></router-outlet>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterModule],
  providers: [...projectFiltersProviders]
})
export class ClearStateWrapperComponent implements OnDestroy {
  private readonly _notesFacade = inject(NotesFacade);
  private readonly _stagesFacade = inject(StagesFacade);
  private readonly _areaMeasurementFacade = inject(AreaFacade);
  private readonly _componentsFacade = inject(ComponentsFacade);
  private readonly _showcaseNotesFacade = inject(ShowcaseNotesFacade);
  private readonly _distanceMeasurementFacade = inject(DistanceFacade);
  private readonly _privilegesFacade = inject(ProjectPrivilegesFacade);
  private readonly _lastKnownMatterportState = inject(
    LastKnownMatterportStateService
  );
  private readonly _lastKnownScanState = inject(LastKnownScanStateService);

  ngOnDestroy(): void {
    this._notesFacade.clearStore();
    this._stagesFacade.clearStore();
    this._privilegesFacade.clearStore();
    this._componentsFacade.clearStore();
    this._showcaseNotesFacade.clearStore();
    this._areaMeasurementFacade.clearStore();
    this._distanceMeasurementFacade.clearStore();
    this._lastKnownMatterportState.clearStore();
    this._lastKnownScanState.clearStore();
  }
}

const publicLinkRoute = (
  environment: Environment,
  options: { embedded: boolean } = { embedded: false }
): Route => ({
  loadChildren: () =>
    import('@simlab/feature/public').then((m) =>
      m.publicFeatureRoutes(environment)
    ),
  providers: [
    {
      provide: API_URL_NEW,
      useValue: environment.configuration.api.url
    },
    {
      provide: API_URL,
      useValue: environment.configuration.api.url
    },
    provideDistanceMeasurment(),
    provideAreaMeasurment(),
    { provide: publicEmbeddedToken, useValue: options.embedded }
  ]
});

export const appRoutes = (environment: Environment): Routes => [
  {
    path: '',
    canActivate: [AutoLoginAllRoutesGuard, CheckUserGuard],
    resolve: [UserPreferencesResolver],
    component: SocketLayoutComponent,
    children: [
      {
        path: 'projects',
        loadChildren: () =>
          import('@simlab/feature/projects').then(
            (m) => m.FeatureProjectsModule
          )
      },
      {
        path: 'project/:projectId',
        component: ClearStateWrapperComponent,
        canActivateChild: [
          (route: ActivatedRouteSnapshot) =>
            // Makes sure the opened project itself is in the store, not only
            // "some" project list (e.g. when coming from the organization pages
            // the project may be outside the list loaded by /projects/list).
            inject(ProjectsFacade).ensureProjectLoaded$(
              route.params['projectId']
            ),
          () => {
            const userPrefFacade = inject(UserPreferenceFacade);
            return userPrefFacade.loaded$.pipe(
              switchMap((loaded: boolean) => {
                return iif(
                  () => loaded,
                  of(true),
                  of(false).pipe(
                    tap(() => {
                      userPrefFacade.initStore();
                    }),
                    switchMap(() => userPrefFacade.userConnectedToProcore$)
                  )
                );
              })
            );
          }
        ],

        children: [
          {
            path: 'stages',
            loadChildren: () =>
              import('@simlab/pages/project').then((m) =>
                m.stagesRoutes(environment)
              )
          },
          {
            path: 'documents',
            loadComponent: () =>
              import('@simlab/feature/documents').then(
                (m) => m.DocumentsPageComponent
              ),
            loadChildren: () =>
              import('@simlab/feature/documents').then((m) => m.documentRoutes)
          }
        ],
        canActivate: [permissionToProjectGuard],

        providers: [
          {
            provide: CURRENT_PROJECT_ID,
            deps: [RouterFacadeService],
            useFactory: () =>
              toSignal(
                inject(RouterFacadeService)
                  .getNthRouterParam$(0)
                  .pipe(map((route) => route?.firstChild?.params['projectId']))
              )
          },
          {
            provide: CURRENT_STAGE_ID,
            deps: [RouterFacadeService],
            useFactory: () =>
              toSignal(
                inject(RouterFacadeService)
                  .getNthRouterParam$(0)
                  .pipe(
                    map((route) => route?.firstChild?.queryParams['stageId'])
                  )
              )
          },
          {
            provide: API_URL_NEW,
            useValue: environment.configuration.api.url
          },
          {
            provide: procoreBaseInfoPayload,
            deps: [ProjectsFacade, RouterFacadeService],
            useFactory: getProjectProcoreInfo
          },
          RFIPreviewConfigurableFieldsetService,
          provideState(privilegesFeatureKey, projectPrivilegesReducer),
          provideEffects(ProjectPrivilegesEffects),
          ProcoreAuthorizationService,
          NotesFacade,
          StagesFacade,
          ShowcaseNotesFacade,
          ProjectPrivilegesFacade,
          provideAreaMeasurment(),
          provideDistanceMeasurment()
        ]
      },
      {
        path: 'invitation',
        loadChildren: () =>
          import('@simlab/feature/invitation').then(
            (m) => m.FeatureInvitationModule
          ),
        canLoad: [QueryGuard.canLoadWithParams(['t', 'n'])]
      },
      {
        path: 'organizations',
        loadChildren: () =>
          import('@simlab/feature/organizations').then((m) => {
            return m.organizationRoutes(environment);
          })
      },
      { path: '', redirectTo: '/projects/list', pathMatch: 'full' }
    ]
  },
  {
    path: 'callback',
    component: CallbackComponent
  },
  {
    path: 'public/:token',
    ...publicLinkRoute(environment)
  },
  {
    // Public link opened from another application (embedded mode):
    // same feature, but the toolbar logo does not navigate anywhere and
    // stages without a synchronized scan are not available.
    path: 'public-embedded/:token',
    ...publicLinkRoute(environment, { embedded: true })
  },
  {
    path: 'payment-summary',
    loadComponent: () =>
      import('@simlab/feature/organizations').then(
        (m) => m.PaymentSummaryComponent
      )
  },
  {
    path: 'home',
    redirectTo: '/projects/list',
    pathMatch: 'full'
  },
  {
    path: 'unity',
    loadChildren: () =>
      import('@simlab/feature/unity').then((m) => m.unityRoutes)
  },
  {
    path: 'callbackMatterport',
    loadChildren: () =>
      import('./components/callback/matterport-api-callback.component').then(
        (m) => m.MatterportApiCallbackModule
      )
  },
  {
    path: 'callbackProcore',
    loadComponent: () =>
      import('./components/callback/procore-callback.component')
  },
  { path: '403', component: ForbiddenComponent },
  { path: '404', component: NotFoundComponent },
  { path: 'error', component: ErrorComponent },
  { path: 'unauthorized', redirectTo: '403', pathMatch: 'full' },
  { path: '**', redirectTo: '404', pathMatch: 'full' }
];
